import React, { createContext, useContext, useState, useCallback } from 'react';

// Mode constants
export const MODES = {
  REFERENCE: 'reference',
  TRAINING: 'training'
};

// Mode configuration
export const MODE_CONFIG = {
  [MODES.REFERENCE]: {
    name: 'Reference Mode',
    label: 'REFERENCE MODE — LIVE WORK SAFE',
    shortLabel: 'Reference Mode — Live Work Safe',
    description: 'Reference-only situational awareness. No procedures or instructions.',
    iconColor: '#3b82f6', // Blue
    bgColor: '#eff6ff',
    borderColor: '#3b82f6',
    placeholder: 'Ask a reference-only question…',
    endpoint: '/api/qa/ask'
  },
  [MODES.TRAINING]: {
    name: 'Training Mode',
    label: 'TRAINING MODE — NOT FOR LIVE WORK',
    shortLabel: 'Training Mode — Not for Live Work',
    description: 'Educational use only. Procedures and scenarios are examples.',
    iconColor: '#f59e0b', // Amber
    bgColor: '#fffbeb',
    borderColor: '#f59e0b',
    placeholder: 'Ask a training or learning question…',
    endpoint: '/api/training/ask'
  }
};

const ModeContext = createContext(null);

export function ModeProvider({ children }) {
  // Default to Reference Mode (safe for live work)
  const [currentMode, setCurrentMode] = useState(MODES.REFERENCE);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [modeSwitchLog, setModeSwitchLog] = useState([]);

  // Log mode switch events (without chat content)
  const logModeEvent = useCallback((event, details = {}) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      ...details
    };
    setModeSwitchLog(prev => [...prev, logEntry]);
    if (process.env.NODE_ENV === "development") {
      console.log('[Mode Event]', logEntry);
    }
  }, []);

  // Request mode switch (shows confirmation modal for Training Mode)
  const requestModeSwitch = useCallback((targetMode) => {
    if (targetMode === currentMode) return;

    logModeEvent('mode_switch_attempt', { from: currentMode, to: targetMode });

    if (targetMode === MODES.TRAINING) {
      // Training Mode requires confirmation
      setPendingMode(targetMode);
      setShowSwitchModal(true);
    } else {
      // Reference Mode can be switched directly
      setCurrentMode(targetMode);
      logModeEvent('mode_switched', { to: targetMode, method: 'direct' });
    }
  }, [currentMode, logModeEvent]);

  // Confirm switch to Training Mode (user selected "NO - not doing live work")
  const confirmTrainingModeSwitch = useCallback((isDoingLiveWork) => {
    logModeEvent('confirmation_selection', { 
      isDoingLiveWork, 
      selection: isDoingLiveWork ? 'YES' : 'NO' 
    });

    if (isDoingLiveWork) {
      // User is doing live work - stay in Reference Mode
      setShowSwitchModal(false);
      setPendingMode(null);
    } else {
      // User is NOT doing live work - switch to Training Mode
      setCurrentMode(MODES.TRAINING);
      setShowSwitchModal(false);
      setPendingMode(null);
      logModeEvent('mode_switched', { to: MODES.TRAINING, method: 'confirmed' });
    }
  }, [logModeEvent]);

  // Cancel mode switch
  const cancelModeSwitch = useCallback(() => {
    setShowSwitchModal(false);
    setPendingMode(null);
    logModeEvent('mode_switch_cancelled');
  }, [logModeEvent]);

  // Force switch to Reference Mode (for live-work detection auto-redirect)
  const forceReferenceMode = useCallback((reason = 'live_work_detected') => {
    if (currentMode !== MODES.REFERENCE) {
      logModeEvent('auto_redirect', { reason, from: currentMode });
      setCurrentMode(MODES.REFERENCE);
    }
  }, [currentMode, logModeEvent]);

  const value = {
    currentMode,
    modeConfig: MODE_CONFIG[currentMode],
    allModes: MODE_CONFIG,
    showSwitchModal,
    pendingMode,
    requestModeSwitch,
    confirmTrainingModeSwitch,
    cancelModeSwitch,
    forceReferenceMode,
    isReferenceMode: currentMode === MODES.REFERENCE,
    isTrainingMode: currentMode === MODES.TRAINING,
    MODES
  };

  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  );
}

export function useMode() {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}

export default ModeContext;
